// src/components/modals/EditSchedulesModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Chip,
  Typography,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  updateSchoolSchedules,
  validateSchedules,
  formatScheduleChange,
  downloadScheduleDeletionZip,
  getScheduleDeletionImpact
} from '../../services/scheduleService';
import PermissionGuard from '../PermissionGuard';

const EditSchedulesModal = ({ open, onClose, school, onSuccess, onNotify }) => {
  const notify = (message, severity = 'info') => {
    if (onNotify) onNotify({ open: true, message, severity });
  };

  const isDraft = !school?.id;
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  const [downloadAffectedOnSave, setDownloadAffectedOnSave] = useState(false);
  const [hasConfirmedLocalDeletion, setHasConfirmedLocalDeletion] = useState(false);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);

  const DAY_OPTIONS = [
    { key: 'monday', label: 'Lun' },
    { key: 'tuesday', label: 'Mar' },
    { key: 'wednesday', label: 'Mié' },
    { key: 'thursday', label: 'Jue' },
    { key: 'friday', label: 'Vie' }
  ];

  const ALL_DAY_KEYS = DAY_OPTIONS.map(d => d.key);

  useEffect(() => {
    if (!open) return;

    const scheduleArray = Array.isArray(school?.schedules)
      ? school.schedules.map((s, i) => ({
        _uid: `existing-${i}`,
        code: s?.code || '',
        originalCode: s?.code || '',
        name: s?.name || '',
        times: Array.isArray(s?.times) ? s.times : ['N/A'],
        days: Array.isArray(s?.days) && s.days.length > 0 ? s.days : ALL_DAY_KEYS
      }))
      : [];

    setSchedules(scheduleArray);
    setConfirmDeleteOpen(false);
    setPendingDeleteIndex(null);
    setDownloadAffectedOnSave(false);
    setHasConfirmedLocalDeletion(false);
    setDeleteImpactLoading(false);
    setDeleteImpact(null);
  }, [open, school?.id]);

  const isAllDaysSelected = (days) => {
    if (!Array.isArray(days) || days.length === 0) return false;
    return ALL_DAY_KEYS.every(day => days.includes(day));
  };

  const handleAddSchedule = () => {
    setSchedules(prev => ([
      ...prev,
      {
        _uid: `new-${Date.now()}-${prev.length}`,
        code: '',
        name: '',
        times: ['N/A'],
        days: [...ALL_DAY_KEYS]
      }
    ]));
  };

  const handleRemoveSchedule = (index) => {
    setSchedules(prev => prev.filter((_, i) => i !== index));
  };

  const requestRemoveSchedule = async (index) => {
    const targetSchedule = schedules[index];
    setPendingDeleteIndex(index);
    // In draft mode (school without id), there's nothing persisted yet.
    if (isDraft) {
      setDeleteImpactLoading(false);
      setDeleteImpact({
        scheduleCode: targetSchedule?.code || null,
        scheduleId: null,
        affectedScheduleSlots: 0,
        affectedStudents: 0,
        affectedFamilies: 0,
        affectedRoutes: 0
      });
      setConfirmDeleteOpen(true);
      return;
    }

    setDeleteImpactLoading(true);
    setDeleteImpact(null);

    try {
      const impact = await getScheduleDeletionImpact(school.id, {
        code: targetSchedule?.code,
        originalCode: targetSchedule?.code
      });
      setDeleteImpact(impact);
    } catch (impactError) {
      console.error('Error loading schedule deletion impact:', impactError);
      setDeleteImpact({
        scheduleCode: targetSchedule?.code || null,
        scheduleId: null,
        affectedScheduleSlots: 0,
        affectedStudents: 0,
        affectedFamilies: 0,
        affectedRoutes: 0
      });
    } finally {
      setDeleteImpactLoading(false);
      setConfirmDeleteOpen(true);
    }
  };

  const cancelRemoveSchedule = () => {
    setConfirmDeleteOpen(false);
    setPendingDeleteIndex(null);
    setDeleteImpact(null);
    setDeleteImpactLoading(false);
  };

  const confirmRemoveSchedule = () => {
    if (pendingDeleteIndex == null) return;
    handleRemoveSchedule(pendingDeleteIndex);
    setHasConfirmedLocalDeletion(true);
    setConfirmDeleteOpen(false);
    setPendingDeleteIndex(null);
    setDeleteImpact(null);
    setDeleteImpactLoading(false);
  };

  const handleScheduleCodeChange = (index, value) => {
    const upper = (value || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);
    setSchedules(prev => {
      const clone = [...prev];
      clone[index] = {
        ...clone[index],
        code: upper,
        name: clone[index].name || (upper ? `HORARIO ${upper}` : '')
      };
      return clone;
    });
  };

  const handleScheduleNameChange = (index, value) => {
    setSchedules(prev => {
      const clone = [...prev];
      clone[index] = { ...clone[index], name: value };
      return clone;
    });
  };

  const handleTimeChange = (index, value) => {
    setSchedules(prev => {
      const clone = [...prev];
      clone[index] = {
        ...clone[index],
        times: [value ? value : 'N/A']
      };
      return clone;
    });
  };

  const handleToggleDay = (scheduleIndex, dayKey) => {
    setSchedules(prev => {
      const clone = [...prev];
      const current = [...clone[scheduleIndex].days];

      if (current.includes(dayKey)) {
        const next = current.filter(d => d !== dayKey);
        if (next.length === 0) return prev; // prevent empty — at least 1 day required
        clone[scheduleIndex] = { ...clone[scheduleIndex], days: next };
      } else {
        const next = [...current, dayKey];
        clone[scheduleIndex] = { ...clone[scheduleIndex], days: next };
      }

      return clone;
    });
  };

  const handleSelectAllDays = (scheduleIndex) => {
    setSchedules(prev => {
      const clone = [...prev];
      clone[scheduleIndex] = {
        ...clone[scheduleIndex],
        days: [...ALL_DAY_KEYS]
      };
      return clone;
    });
  };

  const buildPayloadSchedules = () => {
    return schedules
      .filter(s => s && s.code)
      .map(s => {
        const entry = {
          code: s.code.toUpperCase(),
          name: s.name || `HORARIO ${s.code.toUpperCase()}`,
          times: Array.isArray(s.times) && s.times[0] && s.times[0] !== 'N/A' ? [s.times[0]] : ['N/A'],
          days: s.days
        };

        if (s.originalCode) {
          entry.originalCode = s.originalCode.toUpperCase();
        }

        return entry;
      });
  };

  const validateLocalSchedules = () => {
    const schedulesWithCode = schedules.filter(s => s && s.code);
    const invalidCode = schedulesWithCode.find(s => !/^[A-Z]{2,4}$/.test(s.code));
    if (invalidCode) {
      return `Codigo de horario invalido: "${invalidCode.code}". Use 2 a 4 letras mayusculas (ej: AM, VE).`;
    }

    const codeCounts = {};
    schedulesWithCode.forEach(s => {
      codeCounts[s.code] = (codeCounts[s.code] || 0) + 1;
    });
    const dupCode = Object.keys(codeCounts).find(code => codeCounts[code] > 1);
    if (dupCode) {
      return `Codigo de horario duplicado: "${dupCode}".`;
    }

    const dayTimeMap = new Map();
    const dayLabelMap = {
      monday: 'Lun',
      tuesday: 'Mar',
      wednesday: 'Mie',
      thursday: 'Jue',
      friday: 'Vie'
    };

    for (const sch of schedulesWithCode) {
      const rawTime = Array.isArray(sch.times) ? sch.times[0] : null;
      const time = rawTime && rawTime !== 'N/A' ? rawTime : null;
      if (!time) continue;

      const expandedDays = sch.days;

      for (const day of expandedDays) {
        const key = `${day}::${time}`;
        if (dayTimeMap.has(key)) {
          const existingCode = dayTimeMap.get(key);
          const dayLabel = dayLabelMap[day] || day;
          return `Conflicto entre horarios "${existingCode}" y "${sch.code}". Conflicto entre la hora base y los días aplicables.`;
        }
        dayTimeMap.set(key, sch.code);
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const localError = validateLocalSchedules();
      if (localError) {
        notify(localError, 'error');
        setLoading(false);
        return;
      }

      const payloadSchedules = buildPayloadSchedules();

      // Draft mode (creating school): allow saving with 'N/A' times.
      if (isDraft) {
        const allowedCodes = new Set((payloadSchedules || []).map(s => String(s.code || '').toUpperCase()).filter(Boolean));
        const cleanedRouteSchedules = Array.isArray(school?.routeSchedules)
          ? school.routeSchedules.map((entry) => ({
            ...entry,
            schedules: Array.isArray(entry?.schedules)
              ? entry.schedules
                .map((rs) => ({
                  ...rs,
                  code: rs?.code ? String(rs.code).toUpperCase() : rs?.code
                }))
                .filter((rs) => rs?.code && allowedCodes.has(String(rs.code).toUpperCase()))
              : []
          }))
          : school?.routeSchedules;

        const result = {
          success: true,
          school: {
            ...(school || {}),
            schedules: payloadSchedules,
            // keep any existing draft routeSchedules if present
            routeSchedules: cleanedRouteSchedules
          },
          scheduleChanges: { changes: [] }
        };

        notify('Horarios actualizados en borrador. Se guardarán al crear el colegio.', 'success');
        onSuccess?.(result);
        onClose();
        return;
      }

      const validation = validateSchedules(payloadSchedules);
      if (!validation.valid) {
        notify(validation.errors.join('; '), 'error');
        setLoading(false);
        return;
      }

      const result = await updateSchoolSchedules(school.id, payloadSchedules, {
        confirmScheduleDeletion: hasConfirmedLocalDeletion,
        downloadAffectedStudentsExcel: hasConfirmedLocalDeletion && downloadAffectedOnSave
      });

      if (result?.scheduleDeletionDownload?.available && result?.scheduleDeletionDownload?.token) {
        try {
          await downloadScheduleDeletionZip(result.scheduleDeletionDownload.token);
        } catch (downloadError) {
          console.error('Error downloading affected students ZIP:', downloadError);
          notify('Los horarios se guardaron, pero no se pudo descargar el listado de estudiantes afectados.', 'warning');
        }
      }

      if (result.scheduleChanges?.changes?.length > 0) {
        const changes = result.scheduleChanges.changes
          .map(change => formatScheduleChange(change))
          .join('\n');
        notify(`Horarios actualizados. Cambios propagados:\n${changes}`, 'success');
      } else if (result?.scheduleDeletionDownload?.available) {
        notify('Horarios actualizados exitosamente. Se descargó el listado de estudiantes afectados.', 'success');
      } else {
        notify('Horarios actualizados exitosamente', 'success');
      }

      if (onSuccess) {
        onSuccess(result);
      }

      onClose();
    } catch (err) {
      if (err?.code === 'SCHEDULE_DELETION_CONFIRMATION_REQUIRED') {
        notify('El backend requiere confirmación explícita para eliminar horarios con paradas asociadas. Confirma la eliminación desde el modal de borrar horario e intenta guardar nuevamente.', 'error');
      } else {
        notify(err.message || 'Error al actualizar horarios', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const pendingDeleteSchedule =
    pendingDeleteIndex != null && pendingDeleteIndex >= 0 && pendingDeleteIndex < schedules.length
      ? schedules[pendingDeleteIndex]
      : null;

  const timeToMinutes = (times) => {
    const raw = Array.isArray(times) ? times[0] : null;
    if (!raw || raw === 'N/A') return Infinity;
    const [h, m] = raw.split(':').map(Number);
    return (h * 60) + m;
  };

  const sortedScheduleEntries = useMemo(() => {
    return schedules
      .map((sch, scheduleIndex) => ({ sch, scheduleIndex }))
      .sort((a, b) => timeToMinutes(a.sch.times) - timeToMinutes(b.sch.times));
  }, [schedules]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Editar Horarios de {school?.name || 'Colegio'}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Cambiar la hora base de un horario aquí NO actualiza automáticamente los horarios de alerta configurados por ruta (Asignación de Buses → Horarios). Si mueves una hora, revisa y ajusta esas alertas manualmente si corresponde.
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sortedScheduleEntries.map(({ sch, scheduleIndex }) => {
            const codeError = sch.code && (sch.code.length < 2 || !/^[A-Z]{2,4}$/.test(sch.code));
            const codeDuplicate = sch.code && schedules.filter((s, i) => i !== scheduleIndex && s.code === sch.code).length > 0;

            return (
              <Paper key={sch._uid ?? scheduleIndex} sx={{ p: 2, border: (codeError || codeDuplicate) ? '1px solid #f44336' : '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {sch.code ? `Horario ${sch.code}` : `Horario #${scheduleIndex + 1}`}
                  </Typography>
                  <PermissionGuard permission="colegios-editar">
                    <Tooltip title="Eliminar horario">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => requestRemoveSchedule(scheduleIndex)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </PermissionGuard>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Código"
                      variant="outlined"
                      size="small"
                      value={sch.code || ''}
                      onChange={(e) => handleScheduleCodeChange(scheduleIndex, e.target.value)}
                      placeholder="AM"
                      inputProps={{ maxLength: 4, style: { textTransform: 'uppercase' } }}
                      error={!!(codeError || codeDuplicate)}
                      helperText={
                        codeDuplicate ? 'Código duplicado' :
                        codeError ? '2-4 letras (ej: AM, VE)' : ''
                      }
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={5}>
                    <TextField
                      label="Nombre"
                      variant="outlined"
                      size="small"
                      value={sch.name || ''}
                      onChange={(e) => handleScheduleNameChange(scheduleIndex, e.target.value)}
                      placeholder="HORARIO AM"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Hora base (HH:mm)"
                      variant="outlined"
                      size="small"
                      type="time"
                      value={Array.isArray(sch.times) ? (sch.times[0] === 'N/A' ? '' : sch.times[0]) : ''}
                      onChange={(e) => handleTimeChange(scheduleIndex, e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Días aplicables
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      key="all-days"
                      label="Todos"
                      size="small"
                      variant={isAllDaysSelected(sch.days) ? 'filled' : 'outlined'}
                      color={isAllDaysSelected(sch.days) ? 'primary' : 'default'}
                      onClick={() => handleSelectAllDays(scheduleIndex)}
                      sx={{ cursor: 'pointer' }}
                    />
                    {DAY_OPTIONS.map(({ key, label }) => {
                      const allSelected = isAllDaysSelected(sch.days);
                      const active = allSelected || (Array.isArray(sch.days) && sch.days.includes(key));
                      return (
                        <Chip
                          key={key}
                          label={label}
                          size="small"
                          variant={active ? 'filled' : 'outlined'}
                          color={active ? 'primary' : 'default'}
                          onClick={() => handleToggleDay(scheduleIndex, key)}
                          sx={{ cursor: 'pointer' }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Paper>
            );
          })}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddSchedule}
            sx={{ alignSelf: 'flex-start' }}
          >
            Agregar Horario
          </Button>

          {schedules.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No hay horarios definidos. Haz clic en "Agregar Horario" para crear uno.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <PermissionGuard permission="colegios-editar">
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || schedules.length === 0}
          >
            {loading ? <CircularProgress size={24} /> : 'Guardar Cambios'}
          </Button>
        </PermissionGuard>
      </DialogActions>

      <Dialog open={confirmDeleteOpen} onClose={cancelRemoveSchedule} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminación de horario</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {pendingDeleteSchedule?.code
              ? `¿Seguro que deseas eliminar el horario ${pendingDeleteSchedule.code}?`
              : '¿Seguro que deseas eliminar este horario?'}
          </Typography>
          {deleteImpactLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Calculando estudiantes y familias afectados...
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {`Al eliminar este horario se verán afectados ${deleteImpact?.affectedStudents ?? 0} estudiantes de ${deleteImpact?.affectedFamilies ?? 0} familias.`}
              {(deleteImpact?.affectedRoutes ?? 0) > 0 && (
                ` También se perderá la configuración de alertas de horario de ${deleteImpact.affectedRoutes} ruta${deleteImpact.affectedRoutes > 1 ? 's' : ''} (Asignación de Buses → Horarios), sin posibilidad de recuperarla.`
              )}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Si no confirmas, el horario no se eliminará.
          </Typography>
          <FormControlLabel
            sx={{ display: isDraft ? 'none' : 'flex' }}
            control={(
              <Checkbox
                checked={downloadAffectedOnSave}
                onChange={(e) => setDownloadAffectedOnSave(e.target.checked)}
              />
            )}
            label="Descargar listado de estudiantes afectados al guardar"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRemoveSchedule}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmRemoveSchedule}>
            Eliminar horario
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EditSchedulesModal;
